import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import {
    getCategories,
    resetCategories,
    setAlert,
    setCategoriesMode,
    setHasMore,
    setSelectedPost,
    setShowPostAlbum,
} from '../../../redux/actions';
import CloseButton from '../CloseButton';
import Item from './item';
import { useDispatch, useSelector } from 'react-redux';
import Form from './form';
import { connect } from 'react-redux';
import useDidMountEffect from '../../utils/useDidMountEffect';

import './style.scss';

const MAX_ATTEMPTS = 10;

const CategoryModal = ({
    show = false,
    selectedCategories,
    setSelectedCategories,
    isBusy,
    hasMore,
    categories,
    selectedPost,
    setHasMore,
    mode,
    postAction,
}) => {
    const myUsername = useRef(localStorage.getItem('myusername'));
    const dispatch = useDispatch();
    const observer = useRef();
    const attemptsCount = useRef(0);

    const [perPage, setPerPage] = useState(15);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errors, setErrors] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [activeCategory, setActiveCategory] = useState({});
    const [activeSegment, setActiveSegment] = useState('');
    const { oldCategories = [] } = useSelector(state => state.category);

    const lastCategoryElement = useCallback(node => {
        if (isBusy) return;
        if (observer.current) {
            observer.current.disconnect();
        }
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                fetchCategories();
            }
        });

        if (node) observer.current.observe(node);

        return () => {
            observer.current.disconnect();
        };
    }, [isBusy, hasMore]);

    const fetchCategories = async () => {
        const url = window.location.pathname;
        const urlParts = url.split('/').filter(path => path);
        const segment = urlParts.length > 0 ? urlParts[0].split('?') : '';

        if (segment && segment[0] !== 'home') {
            setActiveSegment(segment[0]);
        }

        let _creator = segment[0];
        if (_creator === '' || _creator === 'home' || _creator === 'create') {
            _creator = myUsername.current;
        }

        dispatch(getCategories(
            {
                perPage,
                categoriesLn: categories.length,
                creator: _creator
            }
        ));
    };

    useEffect(() => {
        dispatch(resetCategories());
    }, []);

    useDidMountEffect(() => {
        if (categories.length <= 0 && attemptsCount.current < MAX_ATTEMPTS) {
            fetchCategories();
            attemptsCount.current += 1;
        }
        if (categories.length > 0) {
            attemptsCount.current = 0;
        }
    }, [categories]);

    const handleHide = () => {
        window.dispatchEvent(new Event('category-modal/hide'));
        dispatch(setShowPostAlbum(false));
        setSelectedCategories(oldCategories.map((item) => item.id));

        if (postAction !== 'edit') {
            dispatch(setSelectedPost({}));
        }

        dispatch(setCategoriesMode(0));
    };

    const handleConfirm = async () => {
        window.dispatchEvent(new Event('category-modal/confirm'));

        if (mode == 0) {
            dispatch(setShowPostAlbum(false));
        } else {
            if (!selectedPost || Object.keys(selectedPost).length <= 0) {
                dispatch(setAlert({
                    type: 'error',
                    body: `Please select a post`,
                    show: true
                }));
                return;
            }

            const postId = selectedPost.id;

            setIsProcessing(true);
            await axios.patch(`/api/posts/${postId}`, {
                categories: selectedCategories,
                update: 'categories'
            })
                .then(response => {
                    setIsProcessing(false);
                    dispatch(setAlert({
                        type: 'success',
                        body: `Post successfully added to album`,
                        show: true
                    }));
                    dispatch(setShowPostAlbum(false));
                    setSelectedCategories([]);
                    dispatch(setSelectedPost({}));
                })
                .catch(error => {
                    setIsProcessing(false);
                });
        }
    };

    useEffect(() => {
        if (selectedCategories.length > 0) {
            setErrors('');
        }
    }, [selectedCategories]);

    const handleCreateNew = () => {
        setShowForm(true);
    };

    const getExistingAlbum = async () => {
        if (selectedPost && Object.keys(selectedPost).length > 0) {
            await axios.get(`/api/posts/${selectedPost.id}/categories`)
                .then(response => {
                    setSelectedCategories(response.data);
                });
        }
    };

    useEffect(() => {
        if (selectedPost && Object.keys(selectedPost).length > 0) {
            getExistingAlbum();
        }
    }, [selectedPost]);

    return <Modal
        show={show}
        onHide={handleHide}
        className="categoryModal"
        backdrop="static"
        centered
    >
        <Modal.Header>
            {showForm ? 'Create New Wall Album' : 'Wall Albums'}
            <CloseButton
                onClick={handleHide}
                style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px'
                }}
                disabled={isProcessing}
            />
            {!showForm && (
                <div className="subheader">
                    Add post to Wall Albums
                </div>
            )}
        </Modal.Header>

        <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)'}}>
            {
                !showForm
                    ? <React.Fragment>
                        <div style={{height: '50vh', overflowY: 'scroll'}}>
                            {
                                errors && <div className='alert alert-danger mb-2'>{errors}</div>
                            }
                            {
                                !isBusy && categories.length > 0 && categories.map((category, index) => {
                                    if (categories.length == index + 1) {
                                        return <Item
                                            innerRef={lastCategoryElement}
                                            category={category}
                                            selectedCategories={selectedCategories}
                                            setSelectedCategories={setSelectedCategories}
                                            setActiveCategory={setActiveCategory}
                                            activeCategory={activeCategory}
                                            setShowForm={setShowForm}
                                            key={category.id}
                                        />;
                                    } else {
                                        return <Item
                                            category={category}
                                            selectedCategories={selectedCategories}
                                            setSelectedCategories={setSelectedCategories}
                                            setActiveCategory={setActiveCategory}
                                            activeCategory={activeCategory}
                                            setShowForm={setShowForm}
                                            key={category.id}
                                        />;
                                    }
                                })
                            }

                            {isBusy && <div style={{
                                textAlign: 'center',
                                marginTop: 12
                            }}>
                                <Spinner size='sm' variant='primary' animation="border" />
                            </div>}
                        </div>
                        <div className="mt-4 text-right">
                            <button
                                className="btn btn-primary mx-1 cm-footer-btn"
                                onClick={handleCreateNew}
                                disabled={isProcessing}
                            >Create New</button>
                            <button
                                className="btn btn-primary mx-1 cm-footer-btn"
                                disabled={isProcessing}
                                onClick={handleConfirm}
                            >Confirm</button>
                        </div>
                    </React.Fragment>
                    : <Form
                        setShowForm={setShowForm}
                        category={activeCategory}
                        setCategory={setActiveCategory}
                    />
            }
        </Modal.Body>
    </Modal >;
};

const mapStateToProps = (state) => {
    return {
        hasMore: state.category.hasMore,
        isBusy: state.category.isBusy,
        categories: state.category.categories,
        mode: state.category.mode,
        selectedPost: state.post.selectedPost,
        postAction: state.post.action,
    };
};

export default connect(mapStateToProps)(CategoryModal);
